/**
 * Medplum options for the provider calendar "new appointment" modal:
 * scope-filtered practitioners, caseload clients, and Episode of Care
 * ("Case") rows where the selected provider (or the logged-in user) is `careManager`.
 *
 * Implementation aligns with caseload RBAC
 * ({@link filterEpisodeOfCareForCaseload}, {@link filterPractitionersForScheduling}).
 */

import type { MedplumClient } from '@medplum/core';
import type { EpisodeOfCare, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCareManagerRef,
  getParticipantRefs,
  getServiceTypeCode,
  getTeamRefs,
} from '@unify/fhir-model/resources/episode-of-care';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
} from '@unify/fhir-model/resources/practitioner';
import { usePermissions } from '../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { useMedplumCaseloadClients } from '../../hooks/useMedplumCaseloadClients';
import {
  chartScopeFromRoleAndPractitioner,
  filterPractitionersForScheduling,
} from '../../lib/rbac/eocChartScope';
import { buildClientSelectOptions } from '../../lib/clientSelectOptions';
import { fetchAssignablePractitioners } from '../../lib/healthcareServiceEntitlement';
import {
  findCanonicalSuperviseeIds,
  isActivePractitioner,
} from '../../lib/supervision/supervisionRelationships';
import { type Client } from '../../pages/caseload/data';

function formatPractitionerName(practitioner: Practitioner): string {
  return formatPractitionerStaffLabel(practitioner) || 'Unknown';
}

function episodeDisplayName(eoc: EpisodeOfCare): string {
  const t = eoc.type?.[0];
  return (
    t?.text?.trim() ||
    t?.coding?.[0]?.display?.trim() ||
    t?.coding?.[0]?.code?.trim() ||
    'Episode of Care'
  );
}

function patientIdFromEpisode(eoc: EpisodeOfCare): string | undefined {
  const ref = eoc.patient?.reference?.trim();
  if (!ref?.startsWith('Patient/')) return undefined;
  return ref.slice('Patient/'.length).trim() || undefined;
}

function resolveClientDisplayForEpisode(eoc: EpisodeOfCare, clients: Client[]): string {
  const pid = patientIdFromEpisode(eoc);
  if (pid) {
    const fromCaseload = clients.find((c) => c.id === pid);
    if (fromCaseload?.name) return fromCaseload.name;
  }
  const display = eoc.patient?.display?.trim();
  if (display) return display;
  return pid ? `Patient ${pid.slice(0, 8)}…` : 'Unknown client';
}

function practitionerEmail(practitioner: Practitioner): string | undefined {
  return practitioner.telecom?.find((t) => t.system === 'email')?.value?.trim() || undefined;
}

/**
 * Practitioner picker options. Shared display names get ` (email)` appended — same rule as the
 * client dropdowns — so two "Alex Kim"s stay distinguishable, and so the meeting Host select
 * (which stores the label as its value) cannot collapse them onto one value.
 */
export function buildPractitionerSelectOptions(
  practitioners: readonly Practitioner[],
  keyBy: 'id' | 'label',
): { value: string; label: string }[] {
  const withId = practitioners.filter((p) => p.id);
  const nameCounts = new Map<string, number>();
  for (const practitioner of withId) {
    const name = formatPractitionerName(practitioner);
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  }

  return withId
    .map((practitioner) => {
      const name = formatPractitionerName(practitioner);
      const email = practitionerEmail(practitioner);
      const label = (nameCounts.get(name) ?? 0) > 1 && email ? `${name} (${email})` : name;
      return { value: keyBy === 'id' ? practitioner.id! : label, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function uniqueClientsFromEpisodeDetails(
  details: ReadonlyArray<Pick<LiveCalendarEpisodeDetail, 'patientId' | 'clientName'>>,
  caseload: ReadonlyArray<Pick<Client, 'id' | 'email'>> = [],
): { id: string; name: string; email?: string }[] {
  const emailById = new Map(caseload.map((c) => [c.id, c.email]));
  const seen = new Map<string, { id: string; name: string; email?: string }>();
  for (const row of details) {
    if (!row.patientId || seen.has(row.patientId)) continue;
    const email = emailById.get(row.patientId);
    seen.set(row.patientId, {
      id: row.patientId,
      name: row.clientName,
      ...(email ? { email } : {}),
    });
  }
  return [...seen.values()];
}

export interface LiveCalendarEpisodeDetail {
  id: string;
  /** Subject patient id (no `Patient/` prefix). */
  patientId: string;
  clientName: string;
  episodeDisplayName: string;
  sessionTypeId: string;
  /** `EpisodeOfCare.careManager` Practitioner id (no prefix) — the episode's primary provider. */
  careManagerId?: string;
  /** `EpisodeOfCare.careManager.display`, used as a label fallback. */
  careManagerDisplay?: string;
  /** `EpisodeOfCare.team` CareTeam ids (no prefix). */
  careTeamIds: string[];
  /** RelatedPerson participants from the episode's `participants` extension
   * (e.g. spouse / family members in couples or family therapy). */
  participantRefs: { id: string; display?: string }[];
  /** `EpisodeOfCare.type` therapy-service-type slug (matches the EoC-type catalog `code`). */
  typeCode?: string;
}

function refId(reference: string | undefined, resourceType: string): string | undefined {
  const prefix = `${resourceType}/`;
  if (!reference?.startsWith(prefix)) return undefined;
  return reference.slice(prefix.length).trim() || undefined;
}

/** True when the appointment modal transitions from closed to open. */
export function shouldRefreshCalendarFormOptionsOnModalOpen(
  prevOpened: boolean,
  modalOpened: boolean,
): boolean {
  return modalOpened && !prevOpened;
}

/** Invalidate caseload caches and reload clients when the modal opens. */
export function refreshCalendarFormOptionsOnModalOpen(
  medplum: Pick<MedplumClient, 'invalidateSearches'>,
  caseloadReload: () => void,
): void {
  medplum.invalidateSearches('EpisodeOfCare');
  medplum.invalidateSearches('Patient');
  caseloadReload();
}

export interface UseCalendarAppointmentFormOptionsResult {
  providerSelectData: { value: string; label: string }[];
  /** All accessible practitioners (unscoped) — for meeting participant selection where you invite colleagues. Values are Practitioner IDs. */
  allProviderSelectData: { value: string; label: string }[];
  clientSelectData: { value: string; label: string }[];
  /** Episodes where `careManager` is the selected scheduling provider (or the viewer). */
  liveEpisodeDetails: LiveCalendarEpisodeDetail[];
  episodeSelectData: { value: string; label: string }[];
  loading: boolean;
  episodesLoading: boolean;
  /** Formatted name for defaulting Primary provider / facilitator when opening the modal. */
  defaultPrimaryProviderLabel: string | null;
  /** Lookup: Practitioner ID → display name. Covers all accessible practitioners. */
  practitionerDisplayById: Map<string, string>;
  /** Preferred-only labels for FHIR Reference.display writes. */
  practitionerPreferredDisplayById: Map<string, string>;
}

export function useCalendarAppointmentFormOptions(
  modalOpened: boolean,
  careManagerPractitionerId?: string | null,
): UseCalendarAppointmentFormOptionsResult {
  const medplum = useMedplum();
  const rbac = usePermissions();
  const { practitioner, loading: practitionerLoading } = useCurrentUserPractitioner();
  const viewerId = practitioner?.id;
  const defaultPrimaryProviderLabel = practitioner ? formatPractitionerName(practitioner) : null;

  const { clients, loading: caseloadLoading, error: caseloadError, reload: caseloadReload } =
    useMedplumCaseloadClients();

  const [practitionerOptions, setPractitionerOptions] = useState<Practitioner[]>([]);
  const [practitionersLoading, setPractitionersLoading] = useState(false);
  const [liveEpisodes, setLiveEpisodes] = useState<EpisodeOfCare[]>([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const prevModalOpened = useRef(false);

  /** Fresh modal options: invalidate caches and reload caseload when the modal opens. */
  useEffect(() => {
    if (shouldRefreshCalendarFormOptionsOnModalOpen(prevModalOpened.current, modalOpened)) {
      refreshCalendarFormOptionsOnModalOpen(medplum, caseloadReload);
      setRefreshToken((t) => t + 1);
    }
    prevModalOpened.current = modalOpened;
  }, [modalOpened, medplum, caseloadReload]);

  const supervisedPractitionerIds = useMemo(() => {
    if (rbac.role !== 'supervisor' || !viewerId) return undefined;
    return findCanonicalSuperviseeIds(practitionerOptions, viewerId);
  }, [rbac.role, viewerId, practitionerOptions]);

  const chartCtx = useMemo(
    () =>
      chartScopeFromRoleAndPractitioner(rbac.role, viewerId, supervisedPractitionerIds),
    [rbac.role, viewerId, supervisedPractitionerIds],
  );

  /** Load active Practitioners once RBAC + viewer are ready. */
  useEffect(() => {
    if (!rbac.ready || practitionerLoading || !viewerId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setPractitionersLoading(true);
        // Paginated: a large practice must not silently lose practitioners past the first page.
        // No `active=true` filter either — token search skips practitioners whose optional
        // `active` field was never stamped, so filter in memory instead.
        const found = await fetchAssignablePractitioners(medplum);
        if (!cancelled) setPractitionerOptions(found.filter(isActivePractitioner));
      } catch (e) {
        console.warn('[calendar form] Practitioner search failed:', e);
        if (!cancelled) setPractitionerOptions([]);
      } finally {
        if (!cancelled) setPractitionersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, rbac.ready, practitionerLoading, viewerId]);

  /** Episodes where the selected (or viewing) practitioner is `careManager`. */
  useEffect(() => {
    const managerId = careManagerPractitionerId || viewerId;
    if (!rbac.ready || !managerId) {
      setLiveEpisodes([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setEpisodesLoading(true);
        medplum.invalidateSearches('EpisodeOfCare');
        const eocs = await medplum.searchResources('EpisodeOfCare', {
          'care-manager': `Practitioner/${managerId}`,
          _count: '200',
        });
        if (!cancelled) setLiveEpisodes(eocs);
      } catch (e) {
        console.warn('[calendar form] EpisodeOfCare search failed:', e);
        if (!cancelled) setLiveEpisodes([]);
      } finally {
        if (!cancelled) setEpisodesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, rbac.ready, viewerId, careManagerPractitionerId, refreshToken]);

  return useMemo(() => {
    const loading =
      !rbac.ready ||
      practitionerLoading ||
      caseloadLoading ||
      practitionersLoading ||
      Boolean(caseloadError);

    const scoped = filterPractitionersForScheduling(practitionerOptions, chartCtx);
    const providerSelectData = buildPractitionerSelectOptions(scoped, 'label');

    const practitionerDisplayById = new Map<string, string>();
    const practitionerPreferredDisplayById = new Map<string, string>();
    for (const p of practitionerOptions) {
      if (p.id) {
        practitionerDisplayById.set(p.id, formatPractitionerName(p));
        const preferredLabel = formatPractitionerPreferredLabel(p);
        if (preferredLabel) practitionerPreferredDisplayById.set(p.id, preferredLabel);
      }
    }

    const allProviderSelectData = buildPractitionerSelectOptions(practitionerOptions, 'id');

    const liveEpisodeDetails: LiveCalendarEpisodeDetail[] = liveEpisodes
      .map((eoc): LiveCalendarEpisodeDetail | null => {
        const pid = patientIdFromEpisode(eoc);
        if (!pid || !eoc.id) return null;
        const episodeDisplayNameStr = episodeDisplayName(eoc);
        const sessionTypeId = '';
        const clientName = resolveClientDisplayForEpisode(eoc, clients);
        const careManagerRef = getCareManagerRef(eoc);
        const careTeamIds = getTeamRefs(eoc)
          .map((ref) => refId(ref.reference, 'CareTeam'))
          .filter((id): id is string => Boolean(id));
        const participantRefs = getParticipantRefs(eoc)
          .map((ref) => {
            const rpId = refId(ref.reference, 'RelatedPerson');
            if (!rpId) return null;
            const display = ref.display?.trim();
            return { id: rpId, ...(display ? { display } : {}) };
          })
          .filter((row): row is { id: string; display?: string } => row != null);
        return {
          id: eoc.id,
          patientId: pid,
          clientName,
          episodeDisplayName: episodeDisplayNameStr,
          sessionTypeId,
          careManagerId: refId(careManagerRef?.reference, 'Practitioner'),
          careManagerDisplay: careManagerRef?.display?.trim() || undefined,
          careTeamIds,
          participantRefs,
          typeCode: getServiceTypeCode(eoc),
        };
      })
      .filter((row): row is LiveCalendarEpisodeDetail => row != null);

    const clientSelectData = buildClientSelectOptions(
      uniqueClientsFromEpisodeDetails(liveEpisodeDetails, clients),
    );

    const episodeSelectData = liveEpisodeDetails.map((row) => ({
      value: row.id,
      label: `${row.clientName} — ${row.episodeDisplayName}`,
    }));

    return {
      providerSelectData,
      allProviderSelectData,
      clientSelectData,
      liveEpisodeDetails,
      episodeSelectData,
      loading,
      episodesLoading,
      defaultPrimaryProviderLabel,
      practitionerDisplayById,
      practitionerPreferredDisplayById,
    };
  }, [
    rbac.ready,
    practitionerLoading,
    caseloadLoading,
    practitionersLoading,
    caseloadError,
    practitionerOptions,
    chartCtx,
    clients,
    liveEpisodes,
    defaultPrimaryProviderLabel,
    episodesLoading,
  ]);
}
